import { useState } from 'react';
import { useApi } from '../../api';
import { useCan } from '../../auth';
import { PageHead, Q, Section, Tag } from '../../ui/kit';
import { CATEGORY_INFO, SaveBar, categoryLabel, lines, useProtectionSettings, useSaveProtection, type ProtectionSettingsResponse, type SaveResult } from './common';

/** Protect → Threat protection → Privacy (P6): /protect/threats/privacy. Off by default. */

interface PrivacySettings { enabled: boolean; categories: string[]; stripParams: string[]; allowDomains: string[] }

export function PrivacyProtection() {
  const q = useProtectionSettings();
  return (
    <>
      <PageHead
        title="Privacy protection"
        sub="Optional: blocks third-party trackers inside the pages people visit and removes click-tracking parameters from links. Off by default — turn it on when your organisation wants it."
      />
      <Q q={q}>{(d) => <PrivacyForm d={d} />}</Q>
    </>
  );
}

function PrivacyForm({ d }: { d: ProtectionSettingsResponse }) {
  const can = useCan();
  const writable = can('settings:write');
  const [p, setP] = useState(d.settings.privacy as PrivacySettings);
  const [params, setParams] = useState((d.settings.privacy.stripParams as string[]).join(', '));
  const [allow, setAllow] = useState((d.settings.privacy.allowDomains as string[]).join('\n'));
  const [saved, setSaved] = useState<SaveResult | null>(null);
  const save = useSaveProtection(setSaved);
  const upd = (x: Partial<PrivacySettings>) => { setP({ ...p, ...x }); setSaved(null); };
  const intel = useApi<{ coverage: Record<string, Record<string, number>> }>('/threat/status', { retry: false });
  const coverage = intel.data?.coverage;
  const hasData = (c: string) => Object.values(coverage?.[c] ?? {}).some((n) => n > 0);
  const off = !p.enabled;

  return (
    <>
      <fieldset className="m-0 p-0 border-0" disabled={!writable}>
        <Section title="Privacy">
          <div className="mt-12">
            <label className="check">
              <input type="checkbox" checked={p.enabled} onChange={(e) => upd({ enabled: e.target.checked })} aria-describedby="privacy-enabled-explain" data-testid="privacy-enabled" />
              <b>Block trackers and strip tracking parameters</b>
            </label>
            <p className="explain m-0 mt-2 ml-24" id="privacy-enabled-explain">
              Only requests a page makes to tracker domains are blocked — never a page someone opens themselves. Browsers report only how many rules are active, never which sites were visited.
            </p>
          </div>
        </Section>
        <Section title="Categories">
          <div className="stack-8 mt-12">
            {d.categories.privacy.map((c) => (
              <div key={c}>
                <label className="check">
                  <input type="checkbox" checked={p.categories.includes(c)} disabled={off} aria-describedby={`privacy-category-${c}-explain`}
                    onChange={(e) => upd({ categories: e.target.checked ? [...p.categories, c] : p.categories.filter((x) => x !== c) })} data-testid={`privacy-category-${c}`} />
                  <b>{categoryLabel(c)}</b>
                </label>
                <span className="sub" id={`privacy-category-${c}-explain`}> — {CATEGORY_INFO[c]?.[1]}</span>
                {coverage && !hasData(c) && <Tag className="ml-8" data-testid={`privacy-coverage-${c}`}>your lists only</Tag>}
              </div>
            ))}
          </div>
          {coverage && !d.categories.privacy.some(hasData) && (
            <p className="explain" data-testid="privacy-no-feed">No enabled threat intelligence feed provides tracker domains, so only the entries of your organisation’s deny lists in these categories are blocked (Threat protection → Threat intel). Removing tracking parameters works without any list.</p>
          )}
        </Section>
        <details className="mt-16">
          <summary><b>Tracking parameters removed from links</b> <span className="sub">{params.split(/[\s,]+/).filter(Boolean).length} parameters (utm_source, fbclid, gclid…)</span></summary>
          <label className="field mt-12">Parameters <span className="hint">comma-separated; removed from the address when a page is opened</span>
            <textarea rows={3} value={params} disabled={off} onChange={(e) => { setParams(e.target.value); setSaved(null); }} data-testid="privacy-strip-params" />
          </label>
        </details>
        <Section title="Never block on these sites">
          <label className="field mt-12">Domains <span className="hint">one per line — use when a site stops working because an embedded service is blocked</span>
            <textarea rows={3} value={allow} disabled={off} onChange={(e) => { setAllow(e.target.value); setSaved(null); }} data-testid="privacy-allow-domains" />
          </label>
        </Section>
        <details className="mt-12">
          <summary><b>Expected impact</b></summary>
          <p className="explain">Pages load the same or faster. Some embedded features that depend on a tracker (comment widgets, “share” buttons, analytics-based consent banners) may stop working on some sites; add those sites above. Removing parameters never changes where a link goes.</p>
        </details>
      </fieldset>
      <SaveBar writable={writable} busy={save.isPending} error={save.error} saved={saved}
        onSave={() => save.mutate({ privacy: { ...p, stripParams: [...new Set(params.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean))], allowDomains: lines(allow) } })} />
    </>
  );
}
